import { StackedBarChart, type BarBucket, type BarSeries } from "@gladis/ui";
import { incomeCategoryLabel, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { IncomeGranularity, IncomePageDto } from "../dto.js";
import { formatMonthOnly } from "@gladis/ui/i18n";

/**
 * Household income, by year or by month, stacked by where it came from.
 *
 * ==========================================================================================
 * THE SERVER OWNS THE BUCKETS AND THIS COMPONENT DRAWS THEM
 * ==========================================================================================
 *
 * No arithmetic happens here — not the monthly equivalent, not the year total, not which months an
 * ended source contributed to. All of it is one reconstruction on the server, and a year is defined
 * as the sum of its twelve months so the two resolutions cannot disagree.
 *
 * A component re-deriving any of it from its own clock could disagree with the figures printed
 * beside it, which is the class of bug this codebase spends its comments on.
 */

/** `YYYY` stays as it is; `YYYY-MM-01` becomes a short month, with the year only where it changes. */
function bucketLabel(key: string, granularity: IncomeGranularity, previous: string | null): string {
  if (granularity === "year") return key;

  const [year, month] = key.split("-") as [string, string];
  const name = formatMonthOnly(Number(year), Number(month));
  // Thirty-six bars with the year on every one is noise; without it entirely, a reader cannot tell
  // which January is which. The year appears when it changes, and on the first bar.
  return previous === null || previous.slice(0, 4) !== year ? `${name} ${year}` : name;
}

export interface IncomeChartProps {
  page: IncomePageDto;
}

export function IncomeChart({ page }: IncomeChartProps): ReactNode {
  const t = useT();
  /*
   * THE CATEGORY'S WORD IS CHOSEN HERE — feature 021. A source series carries the name the household
   * typed and it renders untouched; a category series carries the identifier and nothing else,
   * because a domain service that names "Gift" in English names it in English for both members.
   */
  const series: BarSeries[] = page.chart.series.map((entry) => ({
    key: entry.key,
    label:
      entry.label ??
      (entry.categoryValue === undefined ? entry.key : incomeCategoryLabel(entry.categoryValue)),
  }));

  const buckets: BarBucket[] = page.chart.buckets.map((bucket, index) => ({
    key: bucket.key,
    label: bucketLabel(
      bucket.key,
      page.chart.granularity,
      index === 0 ? null : (page.chart.buckets[index - 1]?.key ?? null),
    ),
    partial: bucket.partial,
    values: bucket.bySeries,
    // Only the current year carries one; the primitive draws it faded above the solid part.
    ...(bucket.projectedBySeries ? { projected: bucket.projectedBySeries } : {}),
  }));

  return <StackedBarChart series={series} buckets={buckets} emptyMessage={t("inc.chart.empty")} />;
}
